import type { ToolCallMessagePartComponent } from "@assistant-ui/react";
import { useThreadRuntime } from "@assistant-ui/react";
import { useEffect, useState } from "react";
import {
  CarIcon,
  FilterIcon,
  FuelIcon,
  GaugeIcon,
  LeafIcon,
  PlayIcon,
  TagIcon,
  ZapIcon,
} from "lucide-react";

import {
  updateConversationContext,
  type VehicleDetailResult,
  type VehicleMotorization,
  type VehicleSearchResult,
} from "@/lib/api";
import { useT } from "@/locale-context";
import { useSidePanelStore } from "@/stores/side-panel-store";
import {
  DetailPlaceholder,
  SearchPlaceholder,
} from "@/components/vehicle-panel-placeholder";

type VehicleToolName = "SearchVehicles" | "GetVehicleDetails";

function isVehicleTool(toolName: string): toolName is VehicleToolName {
  return toolName === "SearchVehicles" || toolName === "GetVehicleDetails";
}

function isSearchResult(value: unknown): value is VehicleSearchResult {
  return (
    typeof value === "object" &&
    value !== null &&
    "results" in (value as Record<string, unknown>)
  );
}

function isDetailResult(value: unknown): value is VehicleDetailResult {
  return (
    typeof value === "object" &&
    value !== null &&
    "model" in (value as Record<string, unknown>)
  );
}

const priceFormatter = new Intl.NumberFormat("nl-BE", {
  style: "currency",
  currency: "EUR",
  maximumFractionDigits: 0,
});

export const VehicleStockCard: ToolCallMessagePartComponent = ({
  toolName,
  toolCallId,
  result,
  status,
}) => {
  const t = useT();

  useEffect(() => {
    if (status?.type !== "complete") return;
    const store = useSidePanelStore.getState();
    if (store.lastOpenedToolCallId === toolCallId) return;

    if (toolName === "SearchVehicles" && isSearchResult(result)) {
      console.log("[VehicleStockCard] opening side panel for SearchVehicles", {
        toolCallId,
        count: result.count,
      });
      store.open({ type: "search", data: result }, toolCallId);
      return;
    }
    if (toolName === "GetVehicleDetails" && isDetailResult(result)) {
      store.open({ type: "detail", data: result }, toolCallId);
    }
  }, [status?.type, toolCallId, toolName, result]);

  if (!isVehicleTool(toolName)) return null;

  if (status?.type === "running") {
    return (
      <div className="my-3 rounded-2xl border border-blue-200 bg-gradient-to-br from-slate-50 to-white p-4 shadow-sm">
        <div className="flex items-center gap-2">
          <div className="size-2 animate-pulse rounded-full bg-blue-500" />
          <p className="font-semibold text-neutral-900 text-sm">
            {toolName === "SearchVehicles"
              ? t("vehicleCard.searching")
              : t("vehicleCard.loadingDetails")}
          </p>
        </div>
      </div>
    );
  }

  if (toolName === "SearchVehicles" && isSearchResult(result)) {
    return <SearchPlaceholder data={result} toolCallId={toolCallId} />;
  }

  if (toolName === "GetVehicleDetails" && isDetailResult(result)) {
    return <DetailPlaceholder data={result} toolCallId={toolCallId} />;
  }

  return null;
};

export function SearchGrid({ data }: { data: VehicleSearchResult }) {
  const runtime = useThreadRuntime({ optional: true });
  const t = useT();
  const [showAll, setShowAll] = useState(false);

  if (data.status !== "ok" || data.results.length === 0) {
    return (
      <div className="my-3 rounded-2xl border border-neutral-200 bg-white p-4 shadow-sm">
        <p className="font-semibold text-neutral-900 text-sm">{t("vehicleCard.noResults")}</p>
        {data.broadening.length > 0 ? (
          <ul className="mt-2 list-inside list-disc text-neutral-700 text-xs">
            {data.broadening.map((suggestion) => (
              <li key={suggestion}>{suggestion}</li>
            ))}
          </ul>
        ) : null}
      </div>
    );
  }

  const handleBookTestDrive = async (name: string) => {
    useSidePanelStore.getState().close();

    // Pin the acceptance type so the orchestrator sees the intent even
    // when the user hasn't opened the detail card. The arrow-prefix
    // message below still conveys the model name; this is best-effort
    // (ChatEndpoints treats null fields as "no change").
    try {
      const win = window as Window & {
        __WILDSTREAM_CONVERSATION_ID__?: string;
      };
      if (win.__WILDSTREAM_CONVERSATION_ID__) {
        await updateConversationContext({
          conversationId: win.__WILDSTREAM_CONVERSATION_ID__,
          acceptanceType: "test-drive",
        });
      }
    } catch {
      // Non-fatal: the arrow-prefix message still conveys intent.
    }

    runtime?.append({
      role: "user",
      content: [{ type: "text", text: `→ Test drive: ${name}` }],
    });
  };

  const filters = data.filters;
  const hasFilters = filters?.brandId || filters?.fuelType || filters?.maxPriceEur;

  const VISIBLE_LIMIT = 2;
  const visibleResults = showAll
    ? data.results
    : data.results.slice(0, VISIBLE_LIMIT);
  const hiddenCount = Math.max(0, data.results.length - VISIBLE_LIMIT);

  return (
    <div className="my-1 space-y-2">
      {hasFilters ? (
        <div className="flex flex-wrap items-center gap-1.5">
          <FilterIcon className="size-3 text-neutral-400" />
          {filters.brandId ? (
            <span className="inline-flex items-center rounded-full border border-blue-200 bg-blue-50 px-2 py-0.5 text-[11px] font-medium text-blue-700 capitalize">
              {filters.brandId}
            </span>
          ) : null}
          {filters.fuelType ? (
            <span className="inline-flex items-center rounded-full border border-emerald-200 bg-emerald-50 px-2 py-0.5 text-[11px] font-medium text-emerald-700">
              {filters.fuelType}
            </span>
          ) : null}
          {filters.maxPriceEur ? (
            <span className="inline-flex items-center rounded-full border border-amber-200 bg-amber-50 px-2 py-0.5 text-[11px] font-medium text-amber-700">
              {t("vehicleCard.filterMaxPrice")} {priceFormatter.format(filters.maxPriceEur)}
            </span>
          ) : null}
          <span className="text-[11px] text-neutral-400">
            {t("vehicleCard.resultsCount", { count: data.count })}
          </span>
        </div>
      ) : null}
      <div className="flex flex-col gap-2">
        {visibleResults.map((model) => {
          const fullName = `${model.makeName} ${model.name}`;
          const heroImage = model.imageFilename ?? model.marketingImages[0];
          const typeLabel =
            model.bodyType ?? model.fuelType ?? filters?.fuelType ?? "";
          return (
            <div
              key={model.id}
              className="overflow-hidden rounded-2xl border border-blue-100 bg-white shadow-[0_18px_29px_rgba(197,205,217,0.32)] transition hover:shadow-lg"
            >
              <div className="flex flex-row gap-3 p-3">
                {/* LEFT: hero image + feature pills */}
                <div className="flex w-[42%] shrink-0 flex-col">
                  <div className="flex flex-1 items-center justify-center">
                    {heroImage ? (
                      <img
                        src={heroImage}
                        alt={fullName}
                        className="aspect-[4/3] w-full object-contain"
                        loading="lazy"
                      />
                    ) : (
                      <div className="flex aspect-[4/3] w-full items-center justify-center rounded-lg bg-neutral-100">
                        <CarIcon className="size-8 text-neutral-400" />
                      </div>
                    )}
                  </div>
                  {model.highlights && model.highlights.length > 0 ? (
                    <div className="mt-2 flex flex-wrap gap-1">
                      {model.highlights.slice(0, 3).map((h) => (
                        <span
                          key={h}
                          className="inline-flex items-center rounded-full border border-neutral-200 bg-white px-2 py-0.5 text-[10px] font-medium text-[#6b7280]"
                        >
                          {h}
                        </span>
                      ))}
                    </div>
                  ) : null}
                </div>

                {/* RIGHT: name/price, specs, tagline, CTA */}
                <div className="flex min-w-0 flex-1 flex-col">
                  <div className="flex items-start justify-between gap-2">
                    <p className="min-w-0 break-words font-bold text-[#353a47] text-sm leading-[1.15]">
                      {fullName}
                    </p>
                    <p className="shrink-0 whitespace-nowrap">
                      <span className="font-bold text-[#00C0E8] text-sm leading-none">
                        {priceFormatter.format(
                          model.monthlyFromEur ?? model.startingPriceEur,
                        )}
                      </span>
                      {model.monthlyFromEur != null ? (
                        <span className="ml-0.5 text-[#6b7280] text-[11px] leading-none">
                          /month
                        </span>
                      ) : null}
                    </p>
                  </div>

                  {typeLabel ? (
                    <p className="mt-0.5 truncate text-[#6b7280] text-[11px] capitalize">
                      {typeLabel}
                    </p>
                  ) : null}

                  <div className="mt-1.5 flex items-center gap-3 text-[#353a47] text-[12px] leading-none">
                    {model.rangeKm != null ? (
                      <span className="inline-flex items-center gap-1">
                        <ZapIcon className="size-3 text-[#353a47]" />
                        {model.rangeKm} km
                      </span>
                    ) : null}
                    {model.horsePower != null ? (
                      <span className="inline-flex items-center gap-1">
                        <GaugeIcon className="size-3 text-[#353a47]" />
                        {model.horsePower} {t("vehicleCard.hp")}
                      </span>
                    ) : model.rangeKm == null ? (
                      <span className="inline-flex items-center gap-1">
                        <GaugeIcon className="size-3 text-[#353a47]" />
                        {model.motorizationCount}{" "}
                        {model.motorizationCount === 1
                          ? t("vehicleCard.motorization")
                          : t("vehicleCard.motorizations")}
                      </span>
                    ) : null}
                  </div>

                  {model.tagLine ? (
                    <p className="mt-1.5 line-clamp-3 text-[#353a47] text-[11px] leading-[1.3]">
                      <span className="text-[#00C0E8]">
                        {t("vehicleCard.whyThisFits")}{" "}
                      </span>
                      {model.tagLine}
                    </p>
                  ) : null}

                  <div className="mt-auto pt-2">
                    <button
                      type="button"
                      onClick={() => handleBookTestDrive(fullName)}
                      style={{ backgroundColor: "#00C0E8", color: "#ffffff" }}
                      className="w-full rounded-full px-4 py-1.5 font-medium text-xs shadow-sm transition hover:brightness-95"
                    >
                      {t("vehicleCard.bookTestDrive")}
                    </button>
                  </div>
                </div>
              </div>
            </div>
          );
        })}
      </div>
      {hiddenCount > 0 ? (
        <div className="flex justify-center pt-1">
          <button
            type="button"
            onClick={() => setShowAll((v) => !v)}
            style={{ color: "#00C0E8", fontSize: "12px" }}
            className="bg-transparent hover:underline"
          >
            {showAll
              ? t("vehicleCard.showLess")
              : t("vehicleCard.showMore", { count: hiddenCount })}
          </button>
        </div>
      ) : null}
    </div>
  );
}

export function DetailCard({ data }: { data: VehicleDetailResult }) {
  const runtime = useThreadRuntime({ optional: true });
  const t = useT();
  const [selectedMotorizationId, setSelectedMotorizationId] = useState<string | null>(
    data.motorizations?.[0]?.id ?? null,
  );
  const [submitting, setSubmitting] = useState<null | "test-drive" | "showroom-visit">(null);
  const [error, setError] = useState<string | null>(null);

  if (data.status !== "ok" || !data.model) {
    return (
      <div className="my-3 rounded-2xl border border-neutral-200 bg-white p-4 shadow-sm">
        <p className="text-neutral-900 text-sm">
          {t("vehicleCard.notFound")}
        </p>
      </div>
    );
  }

  const model = data.model;
  const motorizations = data.motorizations ?? [];
  const selected =
    motorizations.find((m) => m.id === selectedMotorizationId) ?? motorizations[0];
  const minPrice = motorizations.length
    ? Math.min(...motorizations.map((m) => m.startingPriceEur))
    : 0;

  const startBooking = async (acceptanceType: "test-drive" | "showroom-visit") => {
    setError(null);
    setSubmitting(acceptanceType);
    useSidePanelStore.getState().close();

    try {
      // conversationId lives on the runtime side in ApiRuntimeProvider — we
      // can't reach the ref directly, so pin the context via the shared id
      // we get back from the metadata event. For a clean first-turn UX, the
      // append() below carries enough intent; the /context endpoint is a
      // best-effort enrichment.
      const win = window as Window & { __WILDSTREAM_CONVERSATION_ID__?: string };
      if (win.__WILDSTREAM_CONVERSATION_ID__ && selected) {
        await updateConversationContext({
          conversationId: win.__WILDSTREAM_CONVERSATION_ID__,
          motorizationId: selected.id,
          acceptanceType,
        });
      }

      const prompt =
        acceptanceType === "test-drive"
          ? `→ Test drive: ${model.makeName} ${model.name}${selected ? ` (${selected.name})` : ""}`
          : `→ Showroom visit: ${model.makeName} ${model.name}`;

      runtime?.append({
        role: "user",
        content: [{ type: "text", text: prompt }],
      });
    } catch (err) {
      setError(err instanceof Error ? err.message : t("vehicleCard.couldNotUpdate"));
    } finally {
      setSubmitting(null);
    }
  };

  return (
    <div className="my-3 overflow-hidden rounded-2xl border border-blue-200 bg-white shadow-sm">
      {model.imageFilename ?? model.marketingImages[0] ? (
        <img
          src={model.imageFilename ?? model.marketingImages[0]}
          alt={`${model.makeName} ${model.name}`}
          className="aspect-video w-full object-cover"
          loading="lazy"
        />
      ) : null}

      <div className="space-y-3 p-4">
        <div>
          <p className="text-neutral-500 text-xs uppercase">{model.makeName}</p>
          <p className="font-semibold text-neutral-900 text-base leading-tight">
            {model.name}
          </p>
          {model.tagLine ? (
            <p className="mt-1 text-neutral-600 text-xs">{model.tagLine}</p>
          ) : null}
        </div>

        {model.videoUrl ? (
          <a
            href={model.videoUrl}
            target="_blank"
            rel="noreferrer noopener"
            className="inline-flex items-center gap-1.5 text-blue-700 text-xs hover:underline"
          >
            <PlayIcon className="size-3.5" />
            {t("vehicleCard.watchVideo")}
          </a>
        ) : null}

        {model.content?.usps && model.content.usps.length > 0 ? (
          <ul className="space-y-1 text-neutral-700 text-xs">
            {model.content.usps.slice(0, 3).map((usp) => (
              <li key={usp} className="flex items-start gap-1.5">
                <LeafIcon className="mt-0.5 size-3 shrink-0 text-emerald-600" />
                <span>{usp}</span>
              </li>
            ))}
          </ul>
        ) : null}

        {motorizations.length > 0 ? (
          <div className="space-y-1.5">
            <p className="font-medium text-neutral-800 text-xs">
              {t("vehicleCard.chooseMotorization")}
            </p>
            <div className="grid grid-cols-1 gap-1.5 sm:grid-cols-2">
              {motorizations.map((m) => (
                <MotorizationPill
                  key={m.id}
                  motorization={m}
                  selected={m.id === selected?.id}
                  onSelect={() => setSelectedMotorizationId(m.id)}
                />
              ))}
            </div>
          </div>
        ) : null}

        {minPrice > 0 ? (
          <div className="flex items-center gap-1.5 text-neutral-700 text-xs">
            <TagIcon className="size-3.5 text-neutral-500" />
            {t("vehicleCard.from")}
            <span className="font-semibold text-neutral-900">
              {priceFormatter.format(minPrice)}
            </span>
          </div>
        ) : null}

        <div className="flex flex-col gap-2 pt-1 sm:flex-row">
          <button
            type="button"
            onClick={() => startBooking("test-drive")}
            disabled={submitting !== null}
            style={{ backgroundColor: "#00C0E8", color: "#ffffff" }}
            className="flex-1 rounded-lg px-3 py-2 font-semibold text-xs shadow-sm transition hover:brightness-95 disabled:opacity-50"
          >
            {submitting === "test-drive" ? t("vehicleCard.busy") : t("vehicleCard.bookTestDrive")}
          </button>
          <button
            type="button"
            onClick={() => startBooking("showroom-visit")}
            disabled={submitting !== null}
            style={{ backgroundColor: "#ffffff", color: "#00C0E8" }}
            className="flex-1 rounded-lg border border-blue-300 px-3 py-2 font-semibold text-xs shadow-sm transition hover:bg-blue-50 disabled:opacity-50"
          >
            {submitting === "showroom-visit" ? t("vehicleCard.busy") : t("vehicleCard.planShowroom")}
          </button>
        </div>

        {error ? (
          <p className="text-red-700 text-xs">{error}</p>
        ) : null}
      </div>
    </div>
  );
}

function MotorizationPill({
  motorization,
  selected,
  onSelect,
}: {
  motorization: VehicleMotorization;
  selected: boolean;
  onSelect: () => void;
}) {
  const t = useT();
  return (
    <button
      type="button"
      onClick={onSelect}
      className={`flex flex-col gap-0.5 rounded-lg border px-2.5 py-1.5 text-left text-xs transition ${
        selected
          ? "border-blue-500 bg-blue-50"
          : "border-neutral-200 bg-white hover:border-blue-300"
      }`}
    >
      <span className="font-medium text-neutral-900">{motorization.name}</span>
      <span className="flex items-center gap-2 text-[11px] text-neutral-500">
        <FuelIcon className="size-3" />
        {motorization.fuelType}
        {motorization.horsePower ? (
          <>
            <GaugeIcon className="size-3" />
            {motorization.horsePower} {t("vehicleCard.hp")}
          </>
        ) : null}
      </span>
      <span className="text-[11px] text-neutral-600">
        {t("vehicleCard.from")}{priceFormatter.format(motorization.startingPriceEur)}
      </span>
    </button>
  );
}
